import React, { useState, useEffect, useCallback } from 'react';
import { medicalDocuments } from '../services/api';
import Icon from './icons';
import { getDocumentTypeDisplayName, mapAiTypeToCode } from '../utils/documentTypes';
import { fetchDocumentTypes } from '../services/documentTypes';
import ImageViewer from './ImageViewer';
import CameraCapture from './CameraCapture';
import DocumentUploadForm from './DocumentUploadForm';
import ImageSourceModal from './ImageSourceModal';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useTranslation } from 'react-i18next';

interface MedicalDocument {
  document_id: string;
  type: string;
  filename: string;
  timestamp: string;
  notes: string;
  image: string;
  ocr_text: string;
  structured_data: any;
  index?: number;
}

// MedicalImages component
const MedicalImages: React.FC<{ patientId: string }> = ({ patientId }) => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<MedicalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<MedicalDocument | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [showImageSourceModal, setShowImageSourceModal] = useState(false);
  const [capturedImage, setCapturedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Define fetchDocuments with useCallback to prevent unnecessary re-creation
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await medicalDocuments.getAll(patientId);
      
      if (response.success) {
        setDocuments(response.documents || []);
      } else {
        setError(t('medicalImages.loadError'));
      }
    } catch (error: any) {
      console.error('Error fetching medical documents:', error);
      
      // Handle authentication errors through the axios interceptor
      setError(error.message || t('medicalImages.loadErrorDetails'));
    } finally {
      setLoading(false);
    }
  }, [patientId, t]);

  // Fetch document types from backend
  useEffect(() => {
    const loadDocumentTypes = async () => {
      try {
        await fetchDocumentTypes();
      } catch (error) {
        console.error('Error loading document types:', error);
      }
    };

    loadDocumentTypes();
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [patientId, fetchDocuments]);

  const handleDocumentClick = async (documentId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await medicalDocuments.getById(patientId, documentId);
      
      if (response.success) {
        setSelectedDocument(response.document);
      } else {
        setError(t('medicalImages.loadDetailError'));
      }
    } catch (error: any) {
      console.error('Error fetching medical document:', error);
      
      // Handle authentication errors through the axios interceptor
      setError(error.message || t('medicalImages.loadDetailErrorDetails'));
    } finally {
      setLoading(false);
    }
  };

  // Function to close the document detail view on mobile
  const handleCloseDocumentDetail = () => {
    setSelectedDocument(null);
  };

  const handleDeleteDocument = async (documentId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const success = await medicalDocuments.delete(patientId, documentId);
      
      if (success) {
        // Refresh documents list
        await fetchDocuments();
        // Clear selected document if it was deleted
        if (selectedDocument && selectedDocument.document_id === documentId) {
          setSelectedDocument(null);
        }
      } else {
        setError(t('medicalImages.deleteError'));
      }
    } catch (error: any) {
      console.error('Error deleting medical document:', error);
      
      // Handle authentication errors through the axios interceptor
      setError(error.message || t('medicalImages.deleteErrorDetails'));
    } finally {
      setLoading(false);
      // Close the delete confirmation dialog
      setShowDeleteConfirm(false);
      setDocumentToDelete(null);
    }
  };

  // Add function to open delete confirmation dialog
  const openDeleteConfirmation = (documentId: string) => {
    setDocumentToDelete(documentId);
    setShowDeleteConfirm(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN');
  };

  const getDocumentTypeLabel = (type: string) => {
    // Use our utility function to get the display name
    return getDocumentTypeDisplayName(type);
  };

  const getDocumentType = (doc: MedicalDocument) => {
    // First try to get type from structured_data if available
    if (doc.structured_data && doc.structured_data.document_type) {
      // If we have an AI-determined type, map it to our system codes
      const aiType = doc.structured_data.document_type;
      return mapAiTypeToCode(aiType);
    }
    
    // Fall back to the document type field
    return doc.type || 'OTHER';
  };

  // Get a display title for the document
  const getDocumentTitle = (doc: MedicalDocument) => {
    // Use document type as the primary title
    const typeLabel = getDocumentTypeLabel(getDocumentType(doc));
    
    // Add date to make it more specific
    const date = new Date(doc.timestamp);
    const dateStr = date.toLocaleDateString('zh-CN');
    
    return `${typeLabel} (${dateStr})`;
  };

  // Get document type icon based on document type
  const getDocumentTypeIcon = (doc: MedicalDocument, size = 28) => {
    // Get the document type, with fallback logic
    const type = getDocumentType(doc);
    
    // Map document types to icon names for the Icon component
    const iconMap: {[key: string]: string} = {
      'BLOOD_TEST': 'OutlineScience',
      'OPTICAL_EXAM': 'OutlineRemoveRedEye',
      'MEDICAL_RECORD': 'OutlineDescription',
      'PRESCRIPTION': 'OutlineMedicalServices',
      'IMAGING_REPORT': 'OutlineBiotech',
      'LAB_REPORT': 'OutlineScience',
      'DISCHARGE_SUMMARY': 'OutlineLocalHospital',
      'PATHOLOGY_REPORT': 'OutlineBiotech',
      'VACCINATION_RECORD': 'OutlineVaccines',
      'SURGICAL_REPORT': 'OutlineMedicalServices',
      'OTHER': 'OutlineImage'
    };
    
    // Get the icon name from the map or use a default
    const iconName = iconMap[type] || 'OutlineImage';
    
    // Return the Icon component with the appropriate icon name
    return <Icon iconName={iconName} size={size} color="#4a5568" />;
  };

  // Handle camera capture
  const handleCameraCapture = (file: File) => {
    setCapturedImage(file);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target && typeof e.target.result === 'string') {
        setImagePreview(e.target.result);
      }
    };
    reader.readAsDataURL(file);
    
    // Close camera modal and open upload form
    setShowCameraModal(false);
    setShowUploadForm(true);
  };

  // Cancel upload process
  const handleCancelUpload = () => {
    setCapturedImage(null);
    setImagePreview(null);
    setShowCameraModal(false);
    setShowImageSourceModal(false);
    setShowUploadForm(false);
  };

  // Handle document upload
  const handleDocumentUpload = async (documentType: string, notes: string) => {
    if (!capturedImage) {
      setError(t('medicalImages.noImageSelected'));
      setShowUploadForm(false);
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      
      // If documentType is empty, the backend will use AI to detect the type
      const response = await medicalDocuments.upload(patientId, capturedImage, documentType, notes);
      
      if (response.success) {
        // Refresh documents list
        await fetchDocuments();
        
        // Select the newly uploaded document if available
        if (response.document_id) {
          try {
            const docResponse = await medicalDocuments.getById(patientId, response.document_id);
            if (docResponse.success) {
              setSelectedDocument(docResponse.document);
            }
          } catch (error) {
            console.error('Error fetching newly uploaded document:', error);
          }
        } else if (response.documents && response.documents.length > 0) {
          // If the API returns the updated list with the new document, select the first one
          // (assuming the newest document is first in the list)
          setSelectedDocument(response.documents[0]);
        } else {
          // If we can't get the specific document, refresh the list and select the first one
          const docsResponse = await medicalDocuments.getAll(patientId);
          if (docsResponse.success && docsResponse.documents && docsResponse.documents.length > 0) {
            // Sort by timestamp descending to get the newest document
            const sortedDocs = [...docsResponse.documents].sort((a, b) => 
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            );
            setSelectedDocument(sortedDocs[0]);
          }
        }
        
        // Reset state
        setCapturedImage(null);
        setImagePreview(null);
        setShowUploadForm(false);
        
        // Show success message
        setSuccessMessage(t('medicalImages.uploadSuccess'));
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(t('medicalImages.uploadError'));
      }
    } catch (error: any) {
      console.error('Error uploading medical document:', error);
      setError(error.message || t('medicalImages.uploadErrorDetails'));
    }
  };

  if (loading && documents.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  // Render document details section
  const renderDocumentDetails = () => {
    if (!selectedDocument) {
      return (
        <div className="border rounded-md p-6 flex flex-col items-center justify-center h-64 text-gray-500">
          <div style={{ width: '48px', height: '48px', marginBottom: '8px' }}>
            <Icon iconName="OutlineImage" size={48} color="#4a5568" />
          </div>
          <p>{t('medicalImages.selectImage')}</p>
        </div>
      );
    }

    return (
      <div className={`border rounded-md p-4 ${isMobile ? 'fixed inset-0 z-50 bg-white overflow-y-auto' : ''}`}>
        {isMobile && (
          <button
            onClick={handleCloseDocumentDetail}
            className="mb-4 flex items-center text-indigo-600"
          >
            <Icon iconName="OutlineArrowBack" size={20} color="currentColor" className="mr-1" />
            <span>{t('common.back')}</span>
          </button>
        )}
        
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {getDocumentTitle(selectedDocument)}
          </h3>
          <button
            onClick={() => openDeleteConfirmation(selectedDocument.document_id)}
            className="text-red-600 hover:text-red-800"
            title={t('common.delete')}
          >
            <Icon iconName="OutlineDelete" size={20} color="currentColor" />
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <span className="text-sm font-medium text-gray-500">{t('medicalImages.type')}：</span>
            <span className="text-sm text-gray-900">{selectedDocument.structured_data?.document_type || t('medicalImages.unknownType')}</span>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500">{t('medicalImages.date')}：</span>
            <span className="text-sm text-gray-900">{formatDate(selectedDocument.timestamp)}</span>
          </div>
        </div>
        
        {selectedDocument.notes && (
          <div className="mb-4">
            <span className="text-sm font-medium text-gray-500">{t('medicalImages.notes')}：</span>
            <p className="text-sm text-gray-900 mt-1">{selectedDocument.notes}</p>
          </div>
        )}
        
        {/* Summary from structured data */}
        {selectedDocument.structured_data?.summary && (
          <div className="mb-4">
            <span className="text-sm font-medium text-gray-500">{t('medicalImages.summary')}：</span>
            <p className="text-sm text-gray-900 mt-1">{selectedDocument.structured_data.summary}</p>
          </div>
        )}
        
        {/* Key findings from structured data */}
        {selectedDocument.structured_data?.key_findings && (
          <div className="mb-4">
            <span className="text-sm font-medium text-gray-500">{t('medicalImages.keyFindings')}：</span>
            {typeof selectedDocument.structured_data.key_findings === 'string' ? (
              <p className="text-sm text-gray-900 mt-1">{selectedDocument.structured_data.key_findings}</p>
            ) : Array.isArray(selectedDocument.structured_data.key_findings) ? (
              <ul className="text-sm text-gray-900 mt-1 list-disc pl-5">
                {selectedDocument.structured_data.key_findings.map((finding: string, index: number) => (
                  <li key={index}>{finding}</li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
        
        {/* Use the ImageViewer component */}
        <ImageViewer 
          imageData={selectedDocument.image}
          altText={getDocumentTypeLabel(getDocumentType(selectedDocument))}
          placeholderIcon={getDocumentTypeIcon(selectedDocument, 64)}
        />
      </div>
    );
  };

  return (
    <div className="p-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4 relative" role="alert">
          <span className="block sm:inline">{error}</span>
          <button 
            className="absolute top-0 right-0 px-4 py-3"
            onClick={() => setError(null)}
          >
            <span className="sr-only">{t('common.close')}</span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
      
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md mb-4 relative" role="alert">
          <span className="block sm:inline">{successMessage}</span>
          <button 
            className="absolute top-0 right-0 px-4 py-3"
            onClick={() => setSuccessMessage(null)}
          >
            <span className="sr-only">{t('common.close')}</span>
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}
      
      {/* Header with title and camera button */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-900">{t('medicalImages.title')}</h3>
        <button
          onClick={() => setShowImageSourceModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <Icon iconName="OutlineCamera" size={20} color="currentColor" className="mr-2" />
          {t('medicalImages.addImage')}
        </button>
      </div>
      
      <div className="flex flex-col md:flex-row gap-6">
        {/* Documents list - hide on mobile when a document is selected */}
        {!(isMobile && selectedDocument) && (
          <div className="w-full md:w-1/3">
            {documents.length === 0 ? (
              <div className="bg-gray-50 p-4 rounded-md text-center text-gray-500">
                {t('medicalImages.noImages')}
              </div>
            ) : (
              <div className="space-y-3">
                {documents.map((doc) => (
                  <div 
                    key={doc.document_id}
                    className={`border rounded-md p-3 cursor-pointer transition-colors ${
                      selectedDocument?.document_id === doc.document_id 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => handleDocumentClick(doc.document_id)}
                  >
                    <div className="flex items-start">
                      <div className="h-16 w-16 flex items-center justify-center flex-shrink-0">
                        {getDocumentTypeIcon(doc)}
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {getDocumentTitle(doc)}
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatDate(doc.timestamp)}
                        </div>
                        {doc.notes && (
                          <div className="text-xs text-gray-500 mt-1 truncate">
                            {doc.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Document details - full width on mobile, 2/3 on desktop */}
        {(isMobile && selectedDocument) || !isMobile ? (
          <div className={`w-full ${!isMobile ? 'md:w-2/3' : ''}`}>
            {renderDocumentDetails()}
          </div>
        ) : null}
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && documentToDelete && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      {t('medicalImages.deleteImage')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {t('medicalImages.deleteConfirmation')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button 
                  type="button" 
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => handleDeleteDocument(documentToDelete)}
                >
                  {t('medicalImages.confirmDelete')}
                </button>
                <button 
                  type="button" 
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDocumentToDelete(null);
                  }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image source selection modal */}
      <ImageSourceModal
        isOpen={showImageSourceModal}
        onClose={handleCancelUpload}
        onCameraSelect={() => {
          setShowImageSourceModal(false);
          setShowCameraModal(true);
        }}
        onFileSelect={(file) => {
          setCapturedImage(file);
          
          // Create preview
          const reader = new FileReader();
          reader.onload = (e) => {
            if (e.target && typeof e.target.result === 'string') {
              setImagePreview(e.target.result);
            }
          };
          reader.readAsDataURL(file);
          
          // Close image source modal and open upload form
          setShowImageSourceModal(false);
          setShowUploadForm(true);
        }}
      />

      {/* Camera capture modal */}
      {showCameraModal && (
        <CameraCapture 
          onCapture={handleCameraCapture}
          onClose={handleCancelUpload}
        />
      )}

      {/* Document upload form */}
      {showUploadForm && capturedImage && imagePreview && (
        <DocumentUploadForm
          imageFile={capturedImage}
          imagePreview={imagePreview}
          onSubmit={handleDocumentUpload}
          onCancel={handleCancelUpload}
        />
      )}
    </div>
  );
};

export default MedicalImages; 